import { type DOMOutputSpec, type Node, type NodeSpec } from "prosemirror-model";

import { DEFAULT_CELL_ID } from "@moment/prosemirror-utils";

/**
 * A read-only embed of a TLDraw canvas page within a markdown document.
 *
 * In markdown, this is represented as a fenced code block with language "Tldraw":
 *
 *   ```Tldraw
 *   jsonPath:pages/abc123/index.json
 *   ```
 *
 * For collaborative documents, a documentId/pageId pair is used instead:
 *
 *   ```Tldraw
 *   documentId:abc pageId:def workspace:my-workspace
 *   ```
 */
export const tldraw_embed: NodeSpec = {
	attrs: {
		id: { default: DEFAULT_CELL_ID },
		jsonPath: { default: null },
		documentId: { default: null },
		pageId: { default: null },
		workspace: { default: null },
	},
	group: "block",
	atom: true,
	selectable: true,
	draggable: false,
	parseDOM: [
		{
			tag: "div[data-tldraw-embed]",
			getAttrs(dom: string | HTMLElement) {
				if (typeof dom === "string") return {};
				return {
					id: dom.getAttribute("data-id") || DEFAULT_CELL_ID,
					jsonPath: dom.getAttribute("data-json-path") || null,
					documentId: dom.getAttribute("data-document-id") || null,
					pageId: dom.getAttribute("data-page-id") || null,
					workspace: dom.getAttribute("data-workspace") || null,
				};
			},
		},
	],
	toDOM(node: Node) {
		const attrs: Record<string, string> = {
			"data-tldraw-embed": "true",
			"data-id": node.attrs["id"],
		};
		if (node.attrs["jsonPath"]) {
			attrs["data-json-path"] = node.attrs["jsonPath"];
		}
		if (node.attrs["documentId"]) {
			attrs["data-document-id"] = node.attrs["documentId"];
		}
		if (node.attrs["pageId"]) {
			attrs["data-page-id"] = node.attrs["pageId"];
		}
		if (node.attrs["workspace"]) {
			attrs["data-workspace"] = node.attrs["workspace"];
		}
		const dom: DOMOutputSpec = ["div", attrs, "TLDraw Embed"];
		return dom;
	},
};
